/**
 * EditLinkModal
 *
 * Modal for editing an existing relationship's properties.
 * Allows updating connection type, status, label, and notes.
 */

import type { ReactElement } from 'react'
import type {
  CaseEndpoint,
  EndpointRelationship,
  MoveRelationshipRequest,
  QuestionTemplate,
  UpdateRelationshipRequest,
} from '../../types/case-container'
import type { EditLinkFormState } from './use-edit-link-modal'
import {
  ChangeEndpointsSection,
  ConnectionTypeField,
  DirectionDisplay,
  ErrorBanner,
  LabelField,
  ModalActions,
  ModalHeader,
  OpenQuestionPanel,
  RegularNotesField,
  StatusField,
} from './edit-link-modal.parts'
import { useEditLinkForm } from './use-edit-link-modal'

interface EditLinkModalProps {
  relationship: EndpointRelationship
  endpoints: CaseEndpoint[]
  templates: QuestionTemplate[]
  onClose: () => void
  onUpdate: (data: UpdateRelationshipRequest) => void
  updating: boolean
  error: string | null
  onMove?: ((request: MoveRelationshipRequest) => Promise<void>) | undefined
  moving?: boolean
}

interface EditLinkFormProps {
  form: EditLinkFormState
  templates: QuestionTemplate[]
  endpoints: CaseEndpoint[]
  relationship: EndpointRelationship
  onClose: () => void
  onMove?: ((request: MoveRelationshipRequest) => Promise<void>) | undefined
  moving: boolean
  error: string | null
  updating: boolean
}

function EditLinkForm({
  form,
  templates,
  endpoints,
  relationship,
  onClose,
  onMove,
  moving,
  error,
  updating,
}: EditLinkFormProps): ReactElement {
  return (
    <form onSubmit={form.handleSubmit} className="space-y-4">
      <ErrorBanner error={error} />
      <ConnectionTypeField
        value={form.formData.connectionType ?? ''}
        onChange={(value) => {
          form.setFormData({ ...form.formData, connectionType: value })
        }}
      />
      <StatusField value={form.formData.status ?? ''} onChange={form.handleStatusChange} />
      <LabelField
        value={form.formData.label ?? ''}
        onChange={(value) => {
          form.setFormData({ ...form.formData, label: value })
        }}
      />
      {form.isOpenQuestion && (
        <OpenQuestionPanel
          templates={templates}
          formData={form.formData}
          setFormData={form.setFormData}
        />
      )}
      {!form.isOpenQuestion && (
        <RegularNotesField
          value={form.formData.investigationNotes ?? ''}
          onChange={(value) => {
            form.setFormData({ ...form.formData, investigationNotes: value })
          }}
        />
      )}
      {onMove && (
        <ChangeEndpointsSection
          endpoints={endpoints}
          relationship={relationship}
          showChangeEndpoints={form.showChangeEndpoints}
          onToggle={() => {
            form.setShowChangeEndpoints(!form.showChangeEndpoints)
          }}
          newSourceId={form.newSourceId}
          setNewSourceId={form.setNewSourceId}
          newTargetId={form.newTargetId}
          setNewTargetId={form.setNewTargetId}
          moveError={form.moveError}
          hasEndpointChanges={form.hasEndpointChanges}
          hasSelfLoop={form.hasSelfLoop}
          moving={moving}
          onMove={() => {
            void form.handleMoveEndpoints()
          }}
        />
      )}
      <ModalActions onClose={onClose} updating={updating} />
    </form>
  )
}

export function EditLinkModal({
  relationship,
  endpoints,
  templates,
  onClose,
  onUpdate,
  updating,
  error,
  onMove,
  moving = false,
}: EditLinkModalProps): ReactElement {
  const form = useEditLinkForm({ relationship, onClose, onUpdate, ...(onMove ? { onMove } : {}) })
  const sourceEndpoint = endpoints.find((ep) => ep.id === relationship.sourceEndpointId)
  const targetEndpoint = endpoints.find((ep) => ep.id === relationship.targetEndpointId)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <ModalHeader onClose={onClose} />
        <DirectionDisplay sourceEndpoint={sourceEndpoint} targetEndpoint={targetEndpoint} />
        <EditLinkForm
          form={form}
          templates={templates}
          endpoints={endpoints}
          relationship={relationship}
          onClose={onClose}
          onMove={onMove}
          moving={moving}
          error={error}
          updating={updating}
        />
      </div>
    </div>
  )
}
